import { useEffect, useMemo, useState } from 'react';
import { Pagination } from '../common';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import type { ActivityFeedEvent, ActivityWindow } from '@bbbk/schema';
import { useNavigate } from 'react-router-dom';
import { emailKey, WINDOW_EMPTY_PHRASE, type FeedDestination } from './activityFormat';
import { useRecordModal } from '../search/RecordModalProvider';
import { viewToPath } from '../../pages/routeMap';
import { personMatches } from './filter';
import { useAccountModal } from './AccountModalProvider';
import { ActivityTimeline } from './ActivityTimeline';
import { AccountTabSkeleton } from './AccountTabSkeleton';
import { TabPanel, AccountError, TabEmpty } from './tabUi';
import type { Person, RoleFilter } from './types';

const shortName = (email: string) => email.split('@')[0];

// The feed pages deeper than the app's 20-row lists. Since admin section views
// joined the stream (Freddi 2026-07-25) one person's morning can fill 20 rows on
// its own, so a 20-row page would cover about an hour and the ledger's runs
// would be cut apart by the page boundary constantly. 50 keeps a run intact far
// more often; the server caps `limit` at 100.
const FEED_PAGE_SIZE = 50;

// Activity tab — actions, sign-ins and page views, newest first, grouped by day
// and then into per-person runs, inside the house panel. It no longer carries its own person-chip strip: the SHARED
// toolbar (search + role) above the tabs is the one selection, so scoping the
// feed reads the same (q, filter) the People and Heatmap tabs do.
//
// How the shared scope drives a server-paged feed:
//  - EXACTLY one person in scope → scope the feed server-side to that actor
//    (the deep-history path; the old per-person behaviour, now reached by typing
//    a name or drilling from the heatmap).
//  - everyone (default) → the unfiltered stream.
//  - a role group / multi-match → the unfiltered stream, narrowed client-side on
//    the loaded page (the panel meta names the scope; paging walks the stream).
export function ActivityFeedView({
  window,
  q,
  filter,
  people,
}: {
  window: ActivityWindow;
  q: string;
  filter: RoleFilter;
  people: Person[];
}) {
  const [page, setPage] = useState(1);
  // Total over the window (from the first page); kept so paging never re-counts.
  const [total, setTotal] = useState<number | null>(null);

  // The shared toolbar resolved to the in-scope people — one rule (personMatches)
  // for every tab, so "Admins" or "bob" means the same here as on People/Heatmap.
  const scope = useMemo(() => people.filter((p) => personMatches(p, q, filter)), [people, q, filter]);
  const isDefault = q.trim() === '' && filter === 'all';
  const single = scope.length === 1 ? scope[0]!.email : null;
  // Narrow the loaded page client-side only when the scope is neither the
  // everyone-default nor a single server-scoped actor (i.e. a role group).
  const clientFiltered = !isDefault && single === null;

  // Reset paging when the server scope changes (single actor ↔ everyone, window).
  useEffect(() => {
    setPage(1);
    setTotal(null);
  }, [single, window]);

  // One page through the shared SWR engine — a new action (any write logs one)
  // revalidates the feed live, and switching window/scope/page just changes the
  // key. When one person is in scope the server filters to them; otherwise it
  // streams everyone and we narrow on the client.
  const feed = useResource(`activity:feed:${window}:${single ?? ''}:${page}`, () => {
    const params = new URLSearchParams({
      window,
      limit: String(FEED_PAGE_SIZE),
      offset: String((page - 1) * FEED_PAGE_SIZE),
    });
    if (single) params.set('actor', single);
    return api<ActivityFeedEvent[]>(`/activity/feed?${params.toString()}`);
  });
  const feedData = feed.state.data;
  const events = feedData?.data ?? null;

  // Keep the total from whichever page carries it (the first).
  useEffect(() => {
    if (feedData?.page?.total != null) setTotal(feedData.page.total);
  }, [feedData]);

  const modal = useAccountModal();
  // Following a row: a section is a route change, a record is the SAME global
  // modal the global search opens (RecordModalProvider), so a booking reached
  // from the feed and one reached from search are the same screen. Both
  // providers are null outside the admin shell, so every call is guarded.
  const records = useRecordModal();
  const navigate = useNavigate();
  const openDestination = (dest: FeedDestination) => {
    if (!dest) return;
    switch (dest.kind) {
      case 'booking':
        return records?.openBooking(dest.id);
      case 'owner':
        return records?.openOwner(dest.id);
      case 'helper':
        return records?.openHelper(dest.id);
      case 'helperLeave':
        // A holiday IS leave — open that helper's leave view, landing ON the
        // entry the row changed rather than an empty selection.
        return records?.openHelperLeave(dest.id, dest.holidayId);
      case 'section':
        return navigate(viewToPath(dest.view));
    }
  };

  // Current allowlist person per actor (lowercased join). Drives BOTH the role
  // pill (same as People/Heatmap) and whether the avatar is clickable: an actor
  // still on the allowlist resolves to a Person here (avatar → edit-user modal,
  // using the allowlist's own email casing); an off-list actor resolves to
  // undefined and its avatar stays inert.
  const peopleByKey = useMemo(() => {
    const m: Record<string, Person> = {};
    for (const p of people) m[emailKey(p.email)] = p;
    return m;
  }, [people]);

  const scopeSet = useMemo(() => new Set(scope.map((p) => emailKey(p.email))), [scope]);
  const visible = useMemo(() => {
    if (events === null) return null;
    if (!clientFiltered) return events;
    return events.filter((e) => scopeSet.has(emailKey(e.actor)));
  }, [events, clientFiltered, scopeSet]);

  // Human label for the panel meta — names exactly what the feed is scoped to.
  const scopeLabel = isDefault ? 'Everyone' : single ? single : `${scope.length} ${scope.length === 1 ? 'person' : 'people'}`;

  return (
    <TabPanel title="Activity" right={<span className="panel-meta">{scopeLabel} · actions, sign-ins &amp; pages</span>}>
      {feed.state.phase === 'error' ? (
        <AccountError message="Couldn't load activity. Try again." />
      ) : visible === null ? (
        <AccountTabSkeleton label="Loading activity…" rows={8} />
      ) : visible.length === 0 ? (
        // EVERY branch names the period the feed queried — not just the
        // everyone-default. "Nothing from Aoife yet" told someone with activity
        // older than the window that she had never done anything, which is the
        // same bounded-reads-as-all-time problem the rest of this work fixes
        // (Codex + Claude, PR #149). The copy also no longer offers to "widen
        // the window": that selector was removed in aec771f.
        <>
          <TabEmpty
            icon="clock"
            title={
              single
                ? `Nothing from ${shortName(single)} ${WINDOW_EMPTY_PHRASE[window]}`
                : isDefault
                  ? `No activity ${WINDOW_EMPTY_PHRASE[window]}`
                  : `No matching activity ${WINDOW_EMPTY_PHRASE[window]}`
            }
          >
            {single
              ? 'Actions and sign-ins appear here the first time they use the app.'
              : isDefault
                ? 'Check back once the team has been in.'
                : 'No one in this filter has activity on this page — clear the filter, or page on.'}
          </TabEmpty>
          {/* Pagination stays even when the page is empty. A role filter narrows
              the LOADED page client-side, so one busy out-of-scope person can
              fill all 50 server rows and leave nothing visible — without this,
              the matching events on later pages were unreachable (Codex). */}
          <Pagination page={page} total={total} pageSize={FEED_PAGE_SIZE} onPage={setPage} />
        </>
      ) : (
        <>
          <ActivityTimeline
            events={visible}
            peopleByKey={peopleByKey}
            onActorClick={(email) => modal?.openUserEdit(email)}
            onOpen={openDestination}
          />
          <Pagination page={page} total={total} pageSize={FEED_PAGE_SIZE} onPage={setPage} />
        </>
      )}
    </TabPanel>
  );
}
